import { AgGridReact } from "ag-grid-react";
import { MouseEvent, useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";

import { CellEditingStoppedEvent, ColDef, PostSortRowsParams } from "ag-grid-community";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useKSK2120_R01GridSettingContent } from "./KSK2120_R01GridSettingContent";
import { cellStyleMap, defaultColDef, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import { KSK2120_R01Dto } from "./Dto_KSK2120_R01";
import { collectText, equipText, useKSK2120_R01Store, } from "./Store_KSK2120_R01";
import { getItemPopupList } from "src/apis/itemApi";
import { formatLocalDate } from "src/utils/date/dateUtils";
import { headerNames_KSK2120_R01 } from "./Util_KSK2120_R01";
import { useKSK2120_R01CellChange } from "./hooks/useKSK2120_R01CellChange";
import { useKSK2120_R01Modal } from "./hooks/useKSK2120_R01Modal";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";


const COLUMN_STATE_KEY = 'KSK2120_R01-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['feederNo', 'childItemcd', 'itemNm', 'coordinateUsage', 'materialIntype', 'lotQty', 'remark'],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: ['lotNo', 'lotQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	alignLeftCells: [''],
};

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	// hoveredRowIndex: number | null;
	// setHoveredRowIndex: (hoveredRowIndex: number | null) => void;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<KSK2120_R01Dto> | null>;
	defaultKSK2120_R01Row: KSK2120_R01Dto;
}

export const Table_KSK2120_R01 = (props: Props) => {

	const { 
		// hoveredRowIndex, setHoveredRowIndex,
		clickable, setClickable,
		gridRef,
		defaultKSK2120_R01Row,
	} = props;

    const { 
		KSK2120_R01List, setKSK2120_R01List,
		insertKSK2120_R01List,
		removeKSK2120_R01List,
		updateKSK2120_R01List,
		selectedKSK2120_R01List, setSelectedKSK2120_R01List, removeSelectedKSK2120_R01List, addSelectedKSK2120_R01List, initSelectedKSK2120_R01List, allSelectedKSK2120_R01List,
		deletedKSK2120_R01List, removeDeletedKSK2120_R01List, addDeletedKSK2120_R01List,
        columnState, setColumnState,
		originalKSK2120_R01List, setOriginalKSK2120_R01List,
	} = useKSK2120_R01Store();

    const { setCodeSearchModalProps } = useUiuxStore();
    const { materialIntypeList, } = useCommonCodeStore();
	
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const isRowDraggingRef = useRef(false);

    //const gridRef = useRef<AgGridReact>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
		IndexCellRenderer,
	} = useKSK2120_R01GridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: KSK2120_R01List,
		defaultGridData: defaultKSK2120_R01Row,
		selectedRow: selectedKSK2120_R01List,
		originalGridData: originalKSK2120_R01List,
		updateRow: (index: number, data: any) => updateKSK2120_R01List(index, data),
	});

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK2120_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedKSK2120_R01List,
			deletedList: deletedKSK2120_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		selectedKSK2120_R01List, deletedKSK2120_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: KSK2120_R01List,
			rowData: KSK2120_R01List[rowIndex],
			insertRow: insertKSK2120_R01List,
			defaultRowData: defaultKSK2120_R01Row,
			setClickable
		});
	}, [KSK2120_R01List, insertKSK2120_R01List, defaultKSK2120_R01Row, setClickable]);

	const insertRowHandlerProcess = useCallback((rowIndex: number, e: React.MouseEvent) => {
		if (e.ctrlKey) {
			for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
		} else { 
			insertRowHandler(rowIndex, e);
		}
	}, [insertRowHandler]);
	

	const baseColumnDefs: ColDef<KSK2120_R01Dto>[] = useMemo(() => [
		{
			headerName: '', colId: 'rowIndex', width: 60,
			cellStyle: cellStyleMap.rowIndex(),
			cellClass: (p: any) => {
				if (p.data?.inserted === true) return 'inserted-cell';
				if (p.data?.isModified === true) return 'modified-cell';
				return '';
			},
			sortable: false, suppressMovable: true, resizable: false,
			cellRenderer: IndexCellRenderer,
			cellRendererParams: {
				getRowStyleFlagsCallback: (params: any) => getRowStyleFlags(params, {
					deletedList: deletedKSK2120_R01List,
					selectedList: selectedKSK2120_R01List,
					dataList: KSK2120_R01List,
				}),
				handleInsertCallback: insertRowHandlerProcess,
				handleRemoveCallback: (rowData: KSK2120_R01Dto) => {
					if (rowData.valueIndex) {
						// 기존 데이터인 경우 삭제 목록에도 추가 (DB 삭제를 위해)
						if (!rowData.inserted) {
							addDeletedKSK2120_R01List([rowData.valueIndex.toString()]);
						}
						removeKSK2120_R01List(rowData.valueIndex);
					}
				},
			},
		},
		// {
		// 	headerName: '작업지시번호', colId: 'productWorkNo', field: 'productWorkNo', width: 170, 
		// 	editable: false,
		// },
		{
			headerName: '피더번호', colId: 'feederNo', field: 'feederNo', width: 100, 
			editable: false,
		},
		{
			headerName: '자품목코드', colId: 'childItemcd', field: 'childItemcd', width: 150, 
			editable: false,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 300,
			editable: false,
		},
		{
			headerName: '좌표사용량', colId: 'coordinateUsage', field: 'coordinateUsage', width: 110, 
			editable: false,
		},
		{
			headerName: '공급구분', colId: 'materialIntype', field: 'materialIntype', width: 100, 
			...CommonCodeColumn.options(materialIntypeList, false, undefined, undefined),
			editable: false,
		},
		{
			headerName: '장착Lot No', colId: 'lotNo', field: 'lotNo', width: 170, 
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '수량', colId: 'lotQty', field: 'lotQty', width: 100, 
			editable: false,
		},
		{
			headerName: '요청사항', colId: 'remark', field: 'remark', width: 110, 
			editable: false,
			cellStyle: (params) => {
				if (!validateString(params.value)) return { backgroundColor: '#F0F0F0' };
				if (params.value === equipText) return { backgroundColor: '#FFFF99' };		// 장착필요
				if (params.value === collectText) return { backgroundColor: '#FFCCCC' };	// 회수필요
				return null;
			},
		},
	], [IndexCellRenderer, KSK2120_R01List, deletedKSK2120_R01List, selectedKSK2120_R01List, defaultKSK2120_R01Row, setClickable, materialIntypeList]);



	const columnDefs: ColDef<KSK2120_R01Dto>[] = useMemo(() => {
		return GridCustomColumnDefs<KSK2120_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: headerNames_KSK2120_R01,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, columnState, headerNames_KSK2120_R01]);

	const { settingCodeSearchModalProps, handlePopupClose } = useKSK2120_R01Modal({
		updateKSK2120_R01List,
		gridRef,
		KSK2120_R01List,
	});

	const { handleCellChange, handleLotNoSearch } = useKSK2120_R01CellChange({
		KSK2120_R01List,
		updateKSK2120_R01List,
		originalKSK2120_R01List,
		settingCodeSearchModalProps,
		gridRef,
	});

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['', ];

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2120_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

    const handleRowClick = useRowSelectionHandler({
        dataList: KSK2120_R01List,
        idKey: 'valueIndex',
        selectedList: selectedKSK2120_R01List,
        deletedList: deletedKSK2120_R01List,
        addSelected: addSelectedKSK2120_R01List,
        removeSelected: removeSelectedKSK2120_R01List,
        clickable, setClickable
    });

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = ['lotNo'];
	const cellClickActions: CustomCellClickAction<KSK2120_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [createCalendarCellClickHandler]);
	const handleCellClick = CreateCellClickHandler(KSK2120_R01List, 'valueIndex', cellClickActions);

	//더블클릭 액션
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2120_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => (field === 'lotNo' || field === 'lotQty'),
			action: (e, foundData) => {

				// 편집 중인 값 또는 저장된 값 가져오기 (stopEditing 없이)
				const editingCells = gridRef.current?.api?.getEditingCells();
				let cellValue = '';
				
				if (editingCells && editingCells.length > 0) {
					// 현재 편집 중이면 편집 중인 값 가져오기
					const cellEditor = gridRef.current?.api?.getCellEditorInstances()[0];
					if (cellEditor && (cellEditor as any).getValue) {
						cellValue = (cellEditor as any).getValue() || foundData.lotNo || '';
					}
				} 
				
				settingCodeSearchModalProps('Lot_Reel', cellValue, false, 'lotNo', foundData, foundData.valueIndex!);
			}
		},
	], [settingCodeSearchModalProps, KSK2120_R01List]);
	
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2120_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<KSK2120_R01Dto>) => {
		if (isRowDraggingRef.current) return;
		postSortHandler(params, KSK2120_R01List, originalKSK2120_R01List, setKSK2120_R01List);
	}, [KSK2120_R01List, originalKSK2120_R01List,]);

	const pinnedBottomRow = useMemo(() => ([
		{
			rowIndex: '계',
			feederNo: KSK2120_R01List.length.toLocaleString() + "건",
			editable: false,
		}
	]), [KSK2120_R01List]);

	const DtModifiedFunction = useMemo(() => {
		return (date: Date, valueIndex: number) => {
			const originRow = originalKSK2120_R01List.find(item => item.valueIndex === valueIndex);
			if(originRow) {
				if(formatDate(originRow.orderDt) === formatDate(date)) {
					return false;
				}
			}
			return true;
		}
	}, [originalKSK2120_R01List]);

	//+- 버튼 테이블에는 보류(조회하면 select한 행을 초기화)
	// useEffect(() => { ensureSelectedRow(); }, [KSK2120_R01List, selectedKSK2120_R01List]);

    return (
			<GridContextWrapper<KSK2120_R01Dto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setKSK2120_R01List}
				initSelected={initSelectedKSK2120_R01List}
				defaultRowData={defaultKSK2120_R01Row}
				allSelected={allSelectedKSK2120_R01List}
				baseColumnDefs={columnDefs}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<KSK2120_R01Dto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={gridDefaultColDef}


					rowClassRules={createRowClassRules<KSK2120_R01Dto>({
						idKey: 'valueIndex',
						selectedList: selectedKSK2120_R01List,
						deletedList: deletedKSK2120_R01List,
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellEditingStarted={(p) => { /* console.log('edit start', p.rowIndex, p.colDef.field, p.data?.valueIndex); */ }}
					onCellValueChanged={handleCellChange}
					onCellFocused={(p) => {
						const colId = typeof p.column === 'string' ? p.column : p.column?.getColId();
						/* console.log('cell focused', p.rowIndex, colId, p.rowPinned); */
					}}
					pinnedBottomRowData={pinnedBottomRow}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
					onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                    onRowDragEnd={(e: any) => {
                        const api = e.api;
                        // 정렬 해제하여 사용자가 정한 순서를 유지
                        if (api.applyColumnState) {
                            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                        }
                        const reordered: KSK2120_R01Dto[] = [];
                        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                            if (!node.data) return;
                            reordered.push({
                                ...node.data,
                                orderIndex: idx + 1,
                                orderIndexSeq: 0,
                            });
                        });
                        setKSK2120_R01List(reordered);
						isRowDraggingRef.current = false;
                    }}

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setColumnState}				//컬럼 상태 저장
				/>
		</GridContextWrapper>
	)
}